import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Star, Crown, Shield, Zap, ArrowLeft } from 'lucide-react';
import { useApp } from '../context/useApp';
import { subscriptionService } from '../services/firebaseService';
import toast from 'react-hot-toast';

const Plans = () => {
  const navigate = useNavigate();
  const { setSubscription, subscription } = useApp();

  const handleBack = () => {
    navigate(-1); // Navigate back to previous page
  };

  const handlePlanSelection = async (plan) => {
    console.log('Plan button clicked:', plan.name);
    
    // For Free Plan - activate directly without payment
    if (plan.id === 'free') {
      try {
        const result = await subscriptionService.createFreeTrial();
        if (result.success) {
          // Reload subscription data
          const subscriptionResult = await subscriptionService.getCurrentUserSubscription();
          if (subscriptionResult.success && subscriptionResult.data) {
            setSubscription(subscriptionResult.data);
            toast.success('Free plan activated successfully!');
          }
          
          // Check if there's a pending lawyer view to redirect to
          const pendingLawyerView = localStorage.getItem('pendingLawyerView');
          if (pendingLawyerView) {
            try {
              const { lawyerId, lawyer } = JSON.parse(pendingLawyerView);
              localStorage.removeItem('pendingLawyerView');
              navigate(`/lawyer-detail/${lawyerId}`, { state: { lawyer } });
            } catch (error) {
              navigate('/find-lawyer');
            }
          } else {
            navigate('/find-lawyer');
          }
        } else {
          toast.error('Failed to activate free plan');
        }
      } catch (error) {
        console.error('Error activating free plan:', error);
        toast.error('Failed to activate free plan');
      }
      return;
    }
    
    // For paid plans - go to payment page
    try {
      // Create a clean plan object without React elements
      const cleanPlan = {
        id: plan.id,
        name: plan.name,
        price: plan.price,
        description: plan.description,
        features: plan.features,
        buttonText: plan.buttonText
      };
      
      navigate('/payment', { state: { plan: cleanPlan } });
    } catch (error) {
      console.error('Navigation error:', error);
      toast.error('Navigation failed: ' + error.message);
    }
  };
  const plans = [
    {
      id: 'free',
      name: 'Free Plan',
      price: '₹0',
      period: '/month',
      description: 'Perfect for getting started',
      badge: null,
      badgeColor: '',
      features: [
        '10 consultation per month (chat only)',
        'Basic legal document templates',
        'Access to legal FAQ section',
        'Community forum access',
        'Email support (48-hour response)'
      ],
      buttonText: 'Get Started',
      buttonColor: 'bg-purple-600 hover:bg-purple-700',
      icon: <Shield className="w-5 h-5" />,
      popular: false
    },
    {
      id: 'pro',
      name: 'Pro Plan',
      price: '₹999',
      period: '/month',
      description: 'Best for regular legal needs',
      badge: null,
      badgeColor: '',
      features: [
        'Unlimited chat consultations',
        '3 video/call consultations per month',
        'Priority lawyer matching',
        'Advanced legal document templates',
        'Case tracking dashboard',
        'Legal document review (2 docs/month)',
        'Priority email support (24-hour response)',
        'Phone support'
      ],
      buttonText: 'Upgrade to Pro',
      buttonColor: 'bg-purple-600 hover:bg-purple-700',
      icon: <Zap className="w-5 h-5" />,
      popular: false
    },
    {
      id: 'pro-plus',
      name: 'Pro+ Plan',
      price: '₹1,999',
      period: '/month',
      description: 'Most comprehensive solution',
      badge: 'Most Popular',
      badgeColor: 'bg-purple-600 text-white',
      features: [
        'Everything in Pro',
        'Unlimited video/call consultations',
        'Dedicated legal advisor',
        'Unlimited document reviews',
        'Court filing assistance',
        'Legal research reports',
        'Emergency legal support (24/7)',
        'Contract drafting service',
        'Priority booking (same-day slots)'
      ],
      buttonText: 'Upgrade to Pro+',
      buttonColor: 'bg-purple-600 hover:bg-purple-700',
      icon: <Star className="w-5 h-5" />,
      popular: true
    },
    {
      id: 'exclusive',
      name: 'Exclusive Plan',
      price: '₹4,999',
      period: '/month',
      description: 'Elite Legal Network',
      badge: 'Premium',
      badgeColor: 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-black',
      features: [
        'Everything in Pro+',
        'Access to High Court lawyers',
        'Access to Supreme Court lawyers',
        'Senior advocate consultations',
        'Constitutional law experts',
        'PIL (Public Interest Litigation) guidance',
        'Supreme Court case filing support',
        'Appellate court representation',
        'Legal opinion from senior counsel',
        'Direct access to law chambers',
        'Expedited case preparation'
      ],
      buttonText: 'Get Exclusive Access',
      buttonColor: 'bg-gradient-to-r from-yellow-400 to-yellow-600 text-black hover:from-yellow-500 hover:to-yellow-700',
      icon: <Crown className="w-5 h-5" />,
      popular: false
    }
  ];

  return (
    <div className="app-screen-container" style={{ minHeight: '100vh', paddingTop: '0px', paddingBottom: '0px' }}>
      {/* Header */}
      <div className="app-header" style={{ flex: '0 0 auto', padding: '10px' }}>
        <div className="header-content">
          <button
            onClick={handleBack}
            className="flex items-center justify-center p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200"
            aria-label="Go back"
          >
            <ArrowLeft className="w-6 h-6 text-purple-600 dark:text-purple-400" />
          </button>
          <div className="flex-1 text-center pr-12"> {/* Added right padding to center the title */}
            <h1 className="header-title">Subscription Plans</h1>
            <p className="text-sm text-text-muted font-medium">Choose the perfect plan for your legal needs</p>
          </div>
        </div>
      </div>

      {/* Plans Content */}
      <div className="container mobile-padding space-y-4" style={{ flexGrow: 1, padding: '10px' }}>
        <div className="space-y-4">
          {plans.map((plan, index) => (
            <div
              key={plan.id}
              className={`relative bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 ${
                plan.popular ? 'ring-2 ring-purple-500 ring-offset-2 dark:ring-offset-gray-900' : ''
              }`}
            >
              {/* Badge */}
              {plan.badge && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold ${plan.badgeColor}`}>
                    {plan.badge}
                  </span>
                </div>
              )}

              {/* Plan Header */}
              <div className="text-center mb-6">
                <div className="flex items-center justify-center mb-3">
                  <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                    <div className="text-purple-600 dark:text-purple-400">
                      {plan.icon}
                    </div>
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">{plan.name}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{plan.description}</p>
                <div className="flex items-baseline justify-center">
                  <span className={`text-3xl font-bold ${
                    plan.id === 'exclusive' 
                      ? 'text-yellow-500' 
                      : 'text-purple-600 dark:text-purple-400'
                  }`}>
                    {plan.price}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400 ml-1">{plan.period}</span>
                </div>
              </div>

              {/* Features List */}
              <div className="space-y-3 mb-6">
                {plan.features.map((feature, featureIndex) => (
                  <div key={featureIndex} className="flex items-start space-x-3">
                    <div className="flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                    </div>
                    <span className="text-sm text-gray-600 dark:text-gray-300">{feature}</span>
                  </div>
                ))}
              </div>

              {/* Action Button */}
                                               <button 
                  onClick={() => handlePlanSelection(plan)}
                  disabled={subscription && subscription.planId === plan.id}
                  className={`w-full py-3 px-4 rounded-xl text-sm font-medium transition-all duration-200 transform hover:scale-105 ${
                    subscription && subscription.planId === plan.id 
                      ? 'bg-green-600 text-white cursor-not-allowed' 
                      : plan.buttonColor
                  } focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800`}
                >
                  {subscription && subscription.planId === plan.id ? 'Active Plan' : plan.buttonText}
              </button>
               
               
               
               
                            </div>
             ))}
        </div>



        {/* Additional Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 text-center"
        >
          <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Need a Custom Plan?</h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Contact our team for enterprise solutions and custom pricing
          </p>
          <button className="py-2 px-6 bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors duration-200 text-sm font-medium">
            Contact Sales
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default Plans;